import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, doc, setDoc, serverTimestamp, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../Firebase";
import { V3Room, V3Event } from "../types/v3types";
import styled from "styled-components";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 30px;
`;

const Form = styled.form`
  background: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CreateButton = styled(Button)`
  background: #007bff;
  color: white;

  &:hover:not(:disabled) {
    background: #0056b3;
  }
`;

const CancelButton = styled(Button)`
  background: #e0e0e0;
  color: #333;

  &:hover {
    background: #d0d0d0;
  }
`;

const ErrorMessage = styled.div`
  background: #ffe3e3;
  color: #c92a2a;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const InfoMessage = styled.div`
  background: #e3f2fd;
  color: #1976d2;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 10px;
`;

const AdminRoomCreate: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<V3Event[]>([]);
  const [error, setError] = useState("");
  
  const [formData, setFormData] = useState({
    roomName: "",
    description: "",
    eventId: "",
    isPublic: true,
    accessCode: "",
    maxParticipants: "",
    allowSpectators: true,
    allowLateJoin: true,
    autoAdvance: false,
    autoAdvanceDelay: "10",
  });

  useEffect(() => {
    if (!projectId) return;

    const loadEvents = async () => {
      try {
        const eventsQuery = query(
          collection(db, "v3events"),
          where("projectId", "==", projectId),
          where("isActive", "==", true)
        );
        
        const snapshot = await getDocs(eventsQuery);
        const eventData = snapshot.docs.map(doc => ({ ...doc.data(), eventId: doc.id } as V3Event));
        setEvents(eventData);
        
        if (eventData.length > 0) {
          setFormData(prev => ({ ...prev, eventId: eventData[0].eventId }));
        }
      } catch (error) {
        console.error("Error loading events:", error);
      }
    };

    loadEvents();
  }, [projectId]);

  const generateAccessCode = () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setFormData(prev => ({ ...prev, accessCode: code }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId) return;
    
    setError("");
    setLoading(true);

    try {
      const roomId = `room_${Date.now()}`;
      
      const roomData: any = {
        roomId,
        projectId,
        eventId: formData.eventId,
        roomName: formData.roomName,
        hostId: "admin",
        status: "waiting",
        isPublic: formData.isPublic,
        participants: [],
        currentProblemIndex: 0,
        displayMode: "waiting",
        settings: {
          allowSpectators: formData.allowSpectators,
          allowLateJoin: formData.allowLateJoin,
          autoAdvance: formData.autoAdvance,
        },
        createdAt: serverTimestamp(),
      };

      // Add optional fields only if they have values
      if (formData.description) {
        roomData.description = formData.description;
      }
      if (!formData.isPublic && formData.accessCode) {
        roomData.accessCode = formData.accessCode;
      }
      if (formData.maxParticipants) {
        roomData.settings.maxParticipants = Number(formData.maxParticipants);
      }
      if (formData.autoAdvance && formData.autoAdvanceDelay) {
        roomData.settings.autoAdvanceDelay = Number(formData.autoAdvanceDelay);
      }

      await setDoc(doc(db, "v3rooms", roomId), roomData);

      navigate(`/admin/v3/projects/${projectId}/rooms/${roomId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "ルームの作成に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Title>新規ルーム作成</Title>
      
      <Form onSubmit={handleSubmit}>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        {events.length === 0 ? (
          <InfoMessage>
            まずイベントを作成してください。
          </InfoMessage>
        ) : (
          <>
            <FormGroup>
              <Label htmlFor="eventId">イベント選択 *</Label>
              <Select
                id="eventId"
                value={formData.eventId}
                onChange={(e) => setFormData({ ...formData, eventId: e.target.value })}
                required
                disabled={loading}
              >
                {events.map(event => (
                  <option key={event.eventId} value={event.eventId}>
                    {event.eventName}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="roomName">ルーム名 *</Label>
              <Input
                id="roomName"
                type="text"
                value={formData.roomName}
                onChange={(e) => setFormData({ ...formData, roomName: e.target.value })}
                placeholder="例: 第1回戦"
                required
                disabled={loading}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="description">説明（任意）</Label>
              <TextArea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="ルームの説明を入力"
                disabled={loading}
              />
            </FormGroup>

            <FormGroup>
              <CheckboxGroup>
                <Checkbox
                  id="isPublic"
                  type="checkbox"
                  checked={formData.isPublic}
                  onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                  disabled={loading}
                />
                <Label htmlFor="isPublic">公開ルーム（誰でも参加可能）</Label>
              </CheckboxGroup>
            </FormGroup>

            {!formData.isPublic && (
              <FormGroup>
                <Label htmlFor="accessCode">アクセスコード</Label>
                <div style={{ display: "flex", gap: "10px" }}>
                  <Input
                    id="accessCode"
                    type="text"
                    value={formData.accessCode}
                    onChange={(e) => setFormData({ ...formData, accessCode: e.target.value })}
                    placeholder="6文字の英数字"
                    disabled={loading}
                    style={{ flex: 1 }}
                  />
                  <Button type="button" onClick={generateAccessCode} disabled={loading}>
                    生成
                  </Button>
                </div>
              </FormGroup>
            )}

            <FormGroup>
              <Label htmlFor="maxParticipants">最大参加人数（空欄で無制限）</Label>
              <Input
                id="maxParticipants"
                type="number"
                value={formData.maxParticipants}
                onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
                min="1"
                disabled={loading}
              />
            </FormGroup>

            <FormGroup>
              <CheckboxGroup>
                <Checkbox
                  id="allowSpectators"
                  type="checkbox"
                  checked={formData.allowSpectators}
                  onChange={(e) => setFormData({ ...formData, allowSpectators: e.target.checked })}
                  disabled={loading}
                />
                <Label htmlFor="allowSpectators">観戦を許可する</Label>
              </CheckboxGroup>
            </FormGroup>

            <FormGroup>
              <CheckboxGroup>
                <Checkbox
                  id="allowLateJoin"
                  type="checkbox"
                  checked={formData.allowLateJoin}
                  onChange={(e) => setFormData({ ...formData, allowLateJoin: e.target.checked })}
                  disabled={loading}
                />
                <Label htmlFor="allowLateJoin">途中参加を許可する</Label>
              </CheckboxGroup>
            </FormGroup>

            <FormGroup>
              <CheckboxGroup>
                <Checkbox
                  id="autoAdvance"
                  type="checkbox"
                  checked={formData.autoAdvance}
                  onChange={(e) => setFormData({ ...formData, autoAdvance: e.target.checked })}
                  disabled={loading}
                />
                <Label htmlFor="autoAdvance">自動進行を有効にする</Label>
              </CheckboxGroup>
            </FormGroup>

            {formData.autoAdvance && (
              <FormGroup>
                <Label htmlFor="autoAdvanceDelay">自動進行の待機時間（秒）</Label>
                <Input
                  id="autoAdvanceDelay"
                  type="number"
                  value={formData.autoAdvanceDelay}
                  onChange={(e) => setFormData({ ...formData, autoAdvanceDelay: e.target.value })}
                  min="1"
                  disabled={loading}
                />
              </FormGroup>
            )}

            <ButtonGroup>
              <CancelButton
                type="button"
                onClick={() => navigate(`/admin/v3/projects/${projectId}`)}
                disabled={loading}
              >
                キャンセル
              </CancelButton>
              <CreateButton type="submit" disabled={loading || events.length === 0}>
                {loading ? "作成中..." : "ルームを作成"}
              </CreateButton>
            </ButtonGroup>
          </>
        )}
      </Form>
    </Container>
  );
};

export default AdminRoomCreate;